import {noop} from '@benchling/js-utils';
import React from 'react';

import {RESTIdentifiedWarehouseParameter} from 'analysis-tool/data-frame-creation/DataFrameCreationTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import DashboardAndBlockSelectorFormGroup, {
  DashboardAndBlockSelectionState,
  DashboardAndBlockSelectorFormGroupProps,
} from 'insights/analytics-queries/block-selector/DashboardAndBlockSelectorFormGroup';
import ParameterValueFieldList, {
  ParameterValueFieldListProps,
  ParameterValuesState,
} from 'insights/parameters/ParameterValueFieldList';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';
import {JSONValue} from 'util/Types';

import {
  getColumnMappingStepperStep,
  ColumnMappingStepperStepProps,
} from './column-mapping/ColumnMappingStepperStep';
import {DataFrameNameState} from './DataFrameCreationTypes';
import DataFrameNameField, {DataFrameNameFieldProps} from './steps/DataFrameNameField';

/**
 * Similar to RunQueryData, but includes parameter config ids that are needed for step config persistence
 */
export interface PersistedRunQueryData {
  parameterizedSQLQuery: string;
  parameters: Array<RESTIdentifiedWarehouseParameter>;
}

/**
 * State and state setters for the data frame creation configuration variables passed into
 * `CreateDataFrameFromAnalyticsBlockViewSkeleton`. Setters are optional because the presentational
 * view may be a user-editable form (i.e. on step creation) or view-only (i.e. in view step config).
 */
export interface FormState {
  dashboardAndBlockSelectionState: DashboardAndBlockSelectionState;
  dataFrameNameState: DataFrameNameState;
  parameterValuesState: ParameterValuesState;
  setDashboardAndBlockSelectionState?: (newState: DashboardAndBlockSelectionState) => void;
  setDataFrameNameState?: (newState: DataFrameNameState) => void;
  setParameterValuesState?: (newState: ParameterValuesState) => void;
  clearState?: () => void;
}

export function makeSharedParameterValueFieldListProps({
  dashboardAndBlockSelectionState,
  parameterValuesState,
  setParameterValuesState = noop,
}: FormState): Omit<ParameterValueFieldListProps, 'configs' | 'disabled'> & {key: string} {
  return {
    key: `${dashboardAndBlockSelectionState.analyticsDashboardId}.${dashboardAndBlockSelectionState.analyticsQueryId}`,
    selectedValues: parameterValuesState,
    onSelectValue: (configId: string, valueJson: JSONValue) => {
      setParameterValuesState({
        ...parameterValuesState,
        [configId]: valueJson,
      });
    },
  };
}

export interface CreateDataFrameFromAnalyticsBlockViewSkeletonProps {
  stepConfigurationViewTemplateProps: Omit<StepConfigurationViewTemplateProps, 'children'>;
  dashboardAndBlockSelectorFormGroupProps: DashboardAndBlockSelectorFormGroupProps;
  dataFrameNameFieldProps: DataFrameNameFieldProps;
  parameterValueFieldListProps: ParameterValueFieldListProps | null;
  columnMappingProps?: ColumnMappingStepperStepProps;
}

/**
 * Skeleton component so that this component can be reused both to create a dataset from scratch and to
 * view the configuration of an existing dataset initialization step. The props of the children components
 * used here define whether the children components are editable or read-only.
 *
 * @package
 */
export default function CreateDataFrameFromAnalyticsBlockViewSkeleton({
  stepConfigurationViewTemplateProps,
  dashboardAndBlockSelectorFormGroupProps,
  dataFrameNameFieldProps,
  parameterValueFieldListProps,
  columnMappingProps,
}: CreateDataFrameFromAnalyticsBlockViewSkeletonProps): JSX.Element {
  return (
    <StepConfigurationViewTemplate {...stepConfigurationViewTemplateProps}>
      <Stepper>
        <StepperStep title="Select query">
          <DashboardAndBlockSelectorFormGroup {...dashboardAndBlockSelectorFormGroupProps} />
        </StepperStep>
        {columnMappingProps != null && getColumnMappingStepperStep(columnMappingProps)}
        <StepperStep title="Name table">
          <DataFrameNameField {...dataFrameNameFieldProps} />
        </StepperStep>
        {parameterValueFieldListProps != null && (
          <StepperStep title="Specify parameters">
            <ParameterValueFieldList {...parameterValueFieldListProps} />
          </StepperStep>
        )}
      </Stepper>
    </StepConfigurationViewTemplate>
  );
}
